import _ from "lodash";
import { flow, types } from "mobx-state-tree";
import { getEnv } from "~/mobx/utils/getEnv";
import { Pet } from "./Pet";
import type { PetInstance, PetSnapshotIn } from "./Pet";
import type { AxiosResponse } from "axios";
import type { Instance, SnapshotIn, SnapshotOut } from "mobx-state-tree";

export interface PetStoreInstance extends Instance<typeof PetStore> {}
export interface PetStoreSnapshotIn extends SnapshotIn<typeof PetStore> {}
export interface PetStoreSnapshotOut extends SnapshotOut<typeof PetStore> {}

export const PetStore = types
  .model("PetStore", {
    map: types.map(Pet),
  })
  .actions((self) => {
    return {
      processOne(data: PetSnapshotIn) {
        const existing = self.map.get(data.id);
        if (existing) {
          _.mergeWith(existing, data, (_, next) => {
            if (Array.isArray(next)) return next; // Treat arrays like atoms
          });
          return existing;
        }
        return self.map.put(data as PetSnapshotIn);
      },
    };
  })
  .actions((self) => {
    return {
      processMany(dataList: PetSnapshotIn[]) {
        return dataList.map((data) => {
          return self.processOne(data);
        });
      },
    };
  })
  .actions((self) => {
    return {
      getMyPets: flow<PetInstance[], []>(function* getMyPets(): any {
        const env = getEnv(self);

        const response: AxiosResponse = yield env.baseHttp.get("patients/");

        const pets = response.data.data;

        response.data = self.processMany(pets);

        return response.data;
      }),

      getPet: flow<PetInstance, [string]>(function* getPet(id: string): any {
        const env = getEnv(self);

        const response: AxiosResponse = yield env.baseHttp.get(
          `patients/${id}`,
        );

        const pet = response.data.data;

        response.data = self.processOne(pet);

        return response.data;
      }),

      createPet: flow<
        PetInstance,
        [{ name: string; speciesId: string; genderId?: string }]
      >(function* createPet({ name, speciesId, genderId }): any {
        const env = getEnv(self);

        const response: AxiosResponse = yield env.baseHttp.post(`patients`, {
          name: name,
          speciesId,
          genderId,
        });

        const pet = response.data.data;

        response.data = self.processOne(pet);

        return response.data;
      }),
    };
  });
